import { Box, Button, Typography } from '@mui/material';
import React, { useState, useEffect } from 'react';
import TextBox from '../../components/TextBox';
import TextArea from '../../components/TextArea';
import { Add, Numbers } from '@mui/icons-material';
import AutoCompleteBank from '../../components/AutoCompleteBank';
import CustomSnackBar from '../../components/CustomSnackBar';
import DatePickV2 from '../../components/DatePickV2'
import { SettlementsAPI } from '../../services/api';

export default function CreateNote({
  onSave,
  setOpen,
  onAddNote,
  noteToEdit,
  existingNotes = [],
  validateUnique = false,
  logicalWarehouses = [],
}) {
    const [loading, setLoading] = useState(false);
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });
    const [selectedLogical, setSelectedLogical] = useState(null);
    const [note, setNote] = useState({
        number: '',
        logical_warehouse_id: null,
        comment: '',
        date: new Date().toISOString().split('T')[0],
    });

    useEffect(() => {
        if (noteToEdit) {
            const storedLogicalId = noteToEdit.logical_warehouse_id ?? noteToEdit.logical_warehouse?.id ?? null;
            const logicalObject = (logicalWarehouses || []).find(l =>
                storedLogicalId != null && String(l.id) === String(storedLogicalId)
            ) || null;

            setNote({
                number: noteToEdit.number || '',
                logical_warehouse_id: logicalObject?.id || storedLogicalId || null,
                comment: noteToEdit.comment || '',
                date: noteToEdit.date || new Date().toISOString().split('T')[0],
            });
            setSelectedLogical(logicalObject);
        } else {
            setNote({
                number: '',
                logical_warehouse_id: null,
                comment: '',
                date: new Date().toISOString().split('T')[0],
            });
            setSelectedLogical(null);
        }
    }, [noteToEdit, logicalWarehouses]);

    const handleLogicalChange = (name, value) => {
        setSelectedLogical(value || null);
        setNote(prev => ({
            ...prev,
            logical_warehouse_id: value ? value.id : null,
        }));
    };

    const handleChange = (name, value) => {
        setNote(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const logicalOptions = logicalWarehouses || [];
    const hasLogicalOptions = logicalOptions.length > 0;

    const handleSubmit = async () => {
        if (!note.number) return;
        if (!hasLogicalOptions || !note.logical_warehouse_id) return;

        const noteKey = (n) => {
            const logicalId = n.logical_warehouse_id ?? n.logical_warehouse?.id ?? null;
            return `logical:${logicalId ?? 'none'}`;
        };

        if (validateUnique) {
            const currentKey = noteKey(note);
            const existing = existingNotes.find((n, idx) => {
                if (noteToEdit && idx === noteToEdit.index) return false;
                return Number(n.number) === Number(note.number) && noteKey(n) === currentKey;
            });
            if (existing) {
                const linked = { ...existing, comment: note.comment };
                if (noteToEdit) {
                    onSave(noteToEdit.index, linked);
                } else {
                    onAddNote(linked);
                }
                setSnack({ open: true, message: `Nota vinculada con exito!`, severity: 'success' });
                setTimeout(() => setOpen(false), 800);
                setNote({
                    number: '',
                    logical_warehouse_id: null,
                    comment: '',
                    date: new Date().toISOString().split('T')[0],
                });
                setSelectedLogical(null);
                return;
            }
            try {
                await SettlementsAPI.checkReceiveNote(note.number);
            } catch (e) {
                setSnack({ open: true, message: 'Nota duplicada', severity: 'error' });
                return;
            }
        }

        const payload = {
            number: note.number,
            date: note.date,
            comment: note.comment,
            logical_warehouse_id: note.logical_warehouse_id || undefined,
        };

        if (noteToEdit) {
            onSave(noteToEdit.index, payload);
            setSnack({ open: true, message: `Nota actualizada con exito!`, severity: 'success' });
        } else {
            onAddNote(payload);
            setSnack({ open: true, message: `Nota agregada con exito!`, severity: 'success' });
        }

        setTimeout(() => setOpen(false), 800);
        setNote({
            number: '',
            logical_warehouse_id: null,
            comment: '',
            date: new Date().toISOString().split('T')[0],
        });
        setSelectedLogical(null);
    };

    const saveDisabled = loading || !note.number || !note.logical_warehouse_id || !hasLogicalOptions;

    return (
        <Box
            sx={{
                backgroundColor: '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                px: 0,
                py: 0,
                gap: 0,
            }}
        >
            <TextBox
                label="Numero de nota"
                type="number"
                name={'number'}
                icon={Numbers}
                maxLength={20}
                newValue={note.number}
                onChange={handleChange}
            />

            <AutoCompleteBank
                options={logicalOptions}
                getOptionLabel={(option) => option.name}
                onChange={handleLogicalChange}
                name="logical_warehouse"
                value={selectedLogical}
                label='Bodega'
                disabled={!hasLogicalOptions}
            />

            {!hasLogicalOptions && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No hay bodegas l?gicas disponibles
                </Typography>
            )}

            <DatePickV2
                allowFutureDates={false}
                allowHistoricalDates={true}
                label='Fecha nota'
                value={note.date}
                size='small'
                name={'date'}
                onChange={handleChange}
                returnType='iso'
            />
            <TextArea
                label="Comentario"
                value={note.comment}
                name={'comment'}
                onChange={handleChange}
            />
            <Button
                onClick={handleSubmit}
                disabled={saveDisabled}
                sx={{ width: '50%' }}
                fullWidth
                variant="contained"
                startIcon={<Add />}
            >
                {noteToEdit ? 'Guardar cambios' : 'Agregar'}
            </Button>

            <CustomSnackBar
                open={snack.open}
                setOpen={(val) => setSnack(prev => ({ ...prev, open: val }))}
                message={snack.message}
                severity={snack.severity}
            />
        </Box>
    );
}
